// iter119 — Daily Activity page.
//
// Contains three sections, gated by role/mandate:
//   1. My Log (mandate-eligible users) — today's entries + Add Visit CTA
//   2. Team Matrix (managers + VP+) — 7-day (or M/Q/Y) roll-up + drill-in
//   3. VP+ inline enable/disable toggle rendered per matrix row
//
// Photo proof: burnt-in canvas overlay before upload (JPEG carries the
// stamp forever). Geolocation is MANDATORY (client refuses to submit if
// permission is denied — enforced server-side too via lat/lng > 0).

import React, { useEffect, useState, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import axios from 'axios';
import useAuthStore from '../store/authStore';
import { toast } from 'sonner';
import { MapPin, Camera, Plus, CheckCircle, XCircle, Calendar, User, MagnifyingGlass, X, CaretRight, CaretLeft, MapTrifold, ListDashes, DownloadSimple } from '@phosphor-icons/react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';
import fileUrl from '../lib/fileUrl';
import { useTenantPath } from '../lib/tenantPath';

import { InlineSkeleton } from '../components/Skeletons';
// iter127 — in-app lightbox for visit photos.
// Any VisitCard can open the lightbox on click; the parent list (list view
// or drill drawer) tells the provider which visits are in scope so ← → and
// keyboard arrows walk through THAT list, not a global one.
// iter133 — also carries the tenant's `stale_quote_days` setting so the
// LastQuoteChip stops hard-coding 30.
const PhotoLightboxContext = createContext(null);
const usePhotoLightbox = () => useContext(PhotoLightboxContext);
const DailyActivityConfigContext = createContext({ staleQuoteDays: 30 });
const useDailyActivityConfig = () => useContext(DailyActivityConfigContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLES_MANDATE = ['Sales Executive', 'Tender Executive', 'Regional Sales Manager', 'National Sales Manager'];

const fmtDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
};
const fmtDateTime = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DailyActivity() {
  const { user } = useAuthStore();
  const isVpPlus = ['Vice President', 'President'].includes(user?.role);
  const isManager = ['Regional Sales Manager', 'National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const isEligible = ROLES_MANDATE.includes(user?.role);

  const [status, setStatus] = useState(null);
  const [myDays, setMyDays] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [memberDrill, setMemberDrill] = useState(null); // {user_id, user_name}

  // iter127 — global lightbox state. Kept at the page level so it renders
  // over every section (matrix, drill drawer, map popup) without z-index games.
  const [lightbox, setLightbox] = useState(null); // {visits: Visit[], index: number}
  const openLightbox = useCallback((visits, index) => {
    if (!visits || visits.length === 0) return;
    setLightbox({ visits, index: Math.max(0, Math.min(index, visits.length - 1)) });
  }, []);
  const closeLightbox = useCallback(() => setLightbox(null), []);

  // iter133 — pull tenant's stale-quote-days setting once. Falls back to 30.
  // Zero disables the "Draft new quote" CTA entirely (users can turn it off
  // from Company Settings if they find it noisy).
  const [staleQuoteDays, setStaleQuoteDays] = useState(30);
  useEffect(() => {
    axios.get(`${API}/company-settings`)
      .then((r) => {
        const v = parseInt(r.data?.stale_quote_days, 10);
        if (Number.isFinite(v) && v >= 0) setStaleQuoteDays(v);
      })
      .catch(() => {});
  }, []);

  const loadMine = useCallback(async () => {
    if (!isEligible) return;
    try {
      const [s, e] = await Promise.all([
        axios.get(`${API}/daily-activity/status/mine`),
        axios.get(`${API}/daily-activity/my-entries`),
      ]);
      setStatus(s.data);
      setMyDays(e.data.days || []);
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || 'Failed to load your log');
    }
  }, [isEligible]);

  useEffect(() => { loadMine(); }, [loadMine]);

  return (
    <PhotoLightboxContext.Provider value={{ open: openLightbox }}>
    <DailyActivityConfigContext.Provider value={{ staleQuoteDays }}>
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="daily-activity-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">Team · Daily Activity</div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>Daily Activity</h1>
          <p className="text-sm text-[#6D8E87] mt-1">On-site check-ins with geo-stamped photo proof. Minimum one visit per day to be counted <b>Active</b>.</p>
        </div>
      </div>

      {/* Section 1 — My log */}
      {isEligible && (
        <MyLogSection
          status={status}
          days={myDays}
          onAddClick={() => setAddOpen(true)}
          reloadMine={loadMine}
        />
      )}

      {/* Section 2 — Team matrix */}
      {isManager && (
        <TeamMatrixSection
          canToggle={isVpPlus}
          onOpenMember={(m) => setMemberDrill(m)}
        />
      )}

      {/* iter121 — Territory heatmap (managers + VP+) */}
      {isManager && (
        <TerritoryHeatmapSection />
      )}

      {/* No visible sections for the current role */}
      {!isEligible && !isManager && (
        <div className="medical-card p-8 text-center text-[#6D8E87]">
          Daily activity reports don't apply to your role.
        </div>
      )}

      {addOpen && (
        <AddVisitModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { setAddOpen(false); loadMine(); }}
        />
      )}

      {memberDrill && (
        <MemberDrillDrawer
          member={memberDrill}
          onClose={() => setMemberDrill(null)}
        />
      )}

      {lightbox && (
        <PhotoLightbox
          visits={lightbox.visits}
          index={lightbox.index}
          onIndex={(i) => setLightbox((cur) => cur ? { ...cur, index: i } : null)}
          onClose={closeLightbox}
        />
      )}
    </div>
    </DailyActivityConfigContext.Provider>
    </PhotoLightboxContext.Provider>
  );
}

// ────────────────────────────────────────────────────────────────────────
// 1. My log — status card + today's visits + history
// ────────────────────────────────────────────────────────────────────────

function MyLogSection({ status, days, onAddClick, reloadMine }) {
  const [selectedDay, setSelectedDay] = useState(null);

  if (status === null) {
    return <div className="medical-card p-6 text-sm text-[#6D8E87]">Loading your log…</div>;
  }
  if (status && !status.enabled) {
    return (
      <div className="medical-card p-6 border-l-4 border-[#94A3B8]" data-testid="mandate-disabled-card">
        <div className="flex items-start gap-3">
          <XCircle size={22} weight="fill" className="text-[#94A3B8]" />
          <div>
            <p className="text-sm font-semibold text-[#3B5A54]">Daily activity is currently disabled for you</p>
            <p className="text-xs text-[#6D8E87] mt-1">Leadership has excluded you from the mandate. Ask a VP/President if this is unexpected.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="my-log-section">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`medical-card p-4 ${status?.is_active_today ? 'border-l-4 border-emerald-500' : 'border-l-4 border-amber-500'}`}>
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Today</div>
          <div className={`text-2xl font-bold mt-1 ${status?.is_active_today ? 'text-emerald-700' : 'text-amber-700'}`} data-testid="today-status">
            {status?.is_active_today ? 'Active' : 'Inactive'}
          </div>
          <div className="text-xs text-[#3B5A54] mt-1">{status?.today_visits || 0} customer visit{status?.today_visits === 1 ? '' : 's'} logged</div>
        </div>
        <div className="medical-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#6D8E87] font-bold">Streak</div>
          <div className="text-2xl font-bold mt-1 text-[#00857e]" data-testid="streak-days">{status?.streak_days || 0} day{status?.streak_days === 1 ? '' : 's'}</div>
          <div className="text-xs text-[#3B5A54] mt-1">Consecutive active days</div>
        </div>
        <div className="medical-card p-4 flex flex-col justify-center items-center bg-[#00a69c] text-white cursor-pointer hover:bg-[#00857e] transition"
             onClick={onAddClick} data-testid="add-visit-cta">
          <Plus size={26} weight="bold" />
          <div className="text-sm font-bold mt-1">Log new visit</div>
          <div className="text-[11px] opacity-80 mt-0.5">Photo + geo required</div>
        </div>
      </div>

      <div className="medical-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E2ECEA] flex items-center justify-between">
          <div className="text-sm font-bold text-[#052A23]">Your daily log</div>
          <div className="text-[11px] text-[#6D8E87]">Click a day to see visits</div>
        </div>
        {(days || []).length === 0 && (
          <div className="p-6 text-center text-sm text-[#6D8E87]">No visits logged yet. Tap <b>Log new visit</b> above to start.</div>
        )}
        {(days || []).map((d) => (
          <div key={d.visit_date}>
            <button
              onClick={() => setSelectedDay(selectedDay === d.visit_date ? null : d.visit_date)}
              className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[#F4F9F7] transition border-b border-[#E2ECEA] ${selectedDay === d.visit_date ? 'bg-[#F4F9F7]' : ''}`}
              data-testid={`my-day-row-${d.visit_date}`}
            >
              <div className="flex items-center gap-3">
                <Calendar size={16} className="text-[#00a69c]" />
                <span className="text-sm font-semibold text-[#052A23]">{fmtDate(d.visit_date)}</span>
                <span className="text-[11px] text-[#6D8E87]">·  {d.visit_count} customer{d.visit_count === 1 ? '' : 's'}</span>
              </div>
              <StatusPill status={d.status} />
            </button>
            {selectedDay === d.visit_date && (
              <div className="bg-[#F9FCFB] px-4 py-3 border-b border-[#E2ECEA]" data-testid={`my-day-visits-${d.visit_date}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {d.visits.map((v) => <VisitCard key={v.id} visit={v} siblings={d.visits} />)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// 2. Team matrix — 7-day roll-up + inline VP+ toggle
// ────────────────────────────────────────────────────────────────────────

function TeamMatrixSection({ canToggle, onOpenMember }) {
  const [data, setData] = useState(null);
  const [busyToggle, setBusyToggle] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/daily-activity/matrix`);
      setData(r.data);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to load team matrix');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleUser = async (uid, next) => {
    setBusyToggle(uid);
    try {
      await axios.patch(`${API}/daily-activity/settings/${uid}`, { enabled: next });
      toast.success(next ? 'Mandate enabled' : 'Mandate disabled');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Toggle failed');
    } finally {
      setBusyToggle('');
    }
  };

  if (!data) return <div className="medical-card p-6 text-sm text-[#6D8E87]">Loading team matrix…</div>;

  return (
    <div className="space-y-3" data-testid="team-matrix-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Team matrix</h2>
        <span className="text-[11px] text-[#6D8E87]">Window: <b>{fmtDate(data.start)}</b> → <b>{fmtDate(data.end)}</b> · click a member row to drill in</span>
      </div>
      <div className="medical-card overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-[#F4F9F7]">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Member</th>
              {data.days.map((d) => (
                <th key={d} className="text-center px-2 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">
                  {new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </th>
              ))}
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Total</th>
              {canToggle && <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider text-[#3B5A54]">Mandate</th>}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.user_id} className="border-t border-[#D5E8E4] hover:bg-[#F4F9F7]" data-testid={`matrix-row-${r.user_id}`}>
                <td className="px-3 py-2">
                  <button
                    onClick={() => onOpenMember({ user_id: r.user_id, user_name: r.user_name })}
                    className="text-left"
                    data-testid={`matrix-drill-${r.user_id}`}
                  >
                    <div className="text-sm font-medium text-[#052A23] hover:text-[#00857e] flex items-center gap-1">
                      {r.user_name}
                      <CaretRight size={12} className="opacity-60" />
                    </div>
                    <div className="text-[10px] text-[#6D8E87]">{r.user_role}</div>
                  </button>
                </td>
                {r.cells.map((c) => (
                  <td key={c.date} className="px-2 py-2 text-center" data-testid={`matrix-cell-${r.user_id}-${c.date}`}>
                    {!r.enabled ? (
                      <span className="text-[10px] text-[#94A3B8]">—</span>
                    ) : c.count > 0 ? (
                      <span className="inline-block min-w-[26px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold" title={`${c.count} visit${c.count === 1 ? '' : 's'}`}>
                        {c.count}
                      </span>
                    ) : (
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" title="Inactive"></span>
                    )}
                  </td>
                ))}
                <td className="px-3 py-2 text-right font-bold text-[#052A23]">{r.total_visits}</td>
                {canToggle && (
                  <td className="px-3 py-2 text-right">
                    <button
                      disabled={busyToggle === r.user_id}
                      onClick={() => toggleUser(r.user_id, !r.enabled)}
                      className={`relative inline-block w-9 h-5 rounded-full transition ${r.enabled ? 'bg-[#00a69c]' : 'bg-[#D5E8E4]'} disabled:opacity-50`}
                      data-testid={`matrix-toggle-${r.user_id}`}
                      title={r.enabled ? 'Click to exclude from mandate' : 'Click to enforce mandate'}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition ${r.enabled ? 'translate-x-4' : ''}`}></span>
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={data.days.length + 3} className="p-6 text-center text-sm text-[#6D8E87]">No mandate-eligible team members in scope.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// 3. Member drill drawer — daily list, sortable by W/M/Y, click day → visits
// ────────────────────────────────────────────────────────────────────────

function MemberDrillDrawer({ member, onClose }) {
  const [days, setDays] = useState(null);
  const [range, setRange] = useState('week'); // week|month|year|all
  const [selectedDay, setSelectedDay] = useState(null);
  // iter120 — view toggle. List = the day-grouped drill; Map = every visit
  // in the current range plotted on a Leaflet map so a manager can see
  // territory coverage at a glance.
  const [view, setView] = useState('list');

  const start = useMemo(() => {
    const t = new Date();
    if (range === 'week') t.setDate(t.getDate() - 6);
    else if (range === 'month') t.setMonth(t.getMonth() - 1);
    else if (range === 'year') t.setFullYear(t.getFullYear() - 1);
    else return null;
    return t.toISOString().slice(0, 10);
  }, [range]);

  const load = useCallback(async () => {
    setDays(null);
    try {
      const params = start ? { start, end: todayISO() } : {};
      const r = await axios.get(`${API}/daily-activity/members/${member.user_id}/entries`, { params });
      setDays(r.data.days || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load activity');
      setDays([]);
    }
  }, [member.user_id, start]);

  useEffect(() => { load(); }, [load]);

  // Flatten all visits in the current window for the map view.
  const allVisits = useMemo(() => {
    if (!days) return [];
    return days.flatMap((d) => d.visits || []).filter((v) => v.geo_lat && v.geo_lng);
  }, [days]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" data-testid="member-drill-drawer" onClick={onClose}>
      <div className="bg-white w-full max-w-3xl h-full overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E2ECEA] flex items-start justify-between bg-[#F4F9F7]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">Daily activity</div>
            <h2 className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{member.user_name}</h2>
          </div>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="drill-close"><X size={22} /></button>
        </div>
        <div className="px-5 py-3 border-b border-[#E2ECEA] flex items-center gap-2 flex-wrap">
          {['week', 'month', 'year', 'all'].map((r) => (
            <button
              key={r}
              onClick={() => { setRange(r); setSelectedDay(null); }}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${range === r ? 'bg-[#00a69c] text-white' : 'bg-[#F4F9F7] text-[#3B5A54] hover:bg-[#E2ECEA]'}`}
              data-testid={`drill-range-${r}`}
            >
              {r === 'all' ? 'All time' : `Last ${r}`}
            </button>
          ))}
          <div className="ml-auto inline-flex rounded-lg overflow-hidden border border-[#D5E8E4]" data-testid="drill-view-toggle">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 ${view === 'list' ? 'bg-[#00a69c] text-white' : 'bg-white text-[#3B5A54] hover:bg-[#F4F9F7]'}`}
              data-testid="drill-view-list"
            >
              <ListDashes size={12} weight="bold" /> List
            </button>
            <button
              onClick={() => setView('map')}
              disabled={allVisits.length === 0}
              className={`px-3 py-1 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${view === 'map' ? 'bg-[#00a69c] text-white' : 'bg-white text-[#3B5A54] hover:bg-[#F4F9F7]'}`}
              data-testid="drill-view-map"
              title={allVisits.length === 0 ? 'No visits with geolocation in this window' : 'View pins on a map'}
            >
              <MapTrifold size={12} weight="bold" /> Map ({allVisits.length})
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {view === 'map' && (
            <VisitsMap visits={allVisits} />
          )}
          {view === 'list' && (
            <>
              {days === null && <InlineSkeleton />}
              {days !== null && days.length === 0 && (
                <div className="p-8 text-center text-sm text-[#6D8E87]">No visits in this window.</div>
              )}
              {(days || []).map((d) => (
                <div key={d.visit_date} className="border-b border-[#E2ECEA]">
                  <button
                    onClick={() => setSelectedDay(selectedDay === d.visit_date ? null : d.visit_date)}
                    className={`w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[#F4F9F7] transition ${selectedDay === d.visit_date ? 'bg-[#F4F9F7]' : ''}`}
                    data-testid={`drill-day-${d.visit_date}`}
                  >
                    <div className="flex items-center gap-3">
                      <Calendar size={16} className="text-[#00a69c]" />
                      <span className="text-sm font-semibold text-[#052A23]">{fmtDate(d.visit_date)}</span>
                      <span className="text-[11px] text-[#6D8E87]">· {member.user_name}</span>
                      <span className="text-[11px] text-[#3B5A54]">· <b>{d.visit_count}</b> customer{d.visit_count === 1 ? '' : 's'}</span>
                    </div>
                    <StatusPill status={d.status} />
                  </button>
                  {selectedDay === d.visit_date && (
                    <div className="bg-[#F9FCFB] px-5 py-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {d.visits.map((v) => <VisitCard key={v.id} visit={v} big siblings={d.visits} />)}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// iter120 — Leaflet map plotting every visit's geolocation. Auto-fits the
// viewport to include all pins. Popup shows customer + time + a tiny
// photo thumbnail so a manager can spot-check without leaving the map.
function VisitsMap({ visits }) {
  const mapContainer = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapContainer.current || visits.length === 0) return;
    // Init map (once). Repeat renders replace markers only.
    if (!mapRef.current) {
      // Fix leaflet default icon URLs (webpack strips them by default).
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });
      mapRef.current = L.map(mapContainer.current, { attributionControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors',
      }).addTo(mapRef.current);
    }
    const map = mapRef.current;
    // Clear existing markers before re-plot.
    map.eachLayer((layer) => { if (layer instanceof L.Marker) map.removeLayer(layer); });
    const points = visits.map((v) => [v.geo_lat, v.geo_lng]);
    visits.forEach((v) => {
      const marker = L.marker([v.geo_lat, v.geo_lng]).addTo(map);
      const dt = new Date(v.captured_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
      const photoSrc = v.photo_storage_path ? fileUrl(v.photo_storage_path) : '';
      const thumb = photoSrc
        ? `<a href="${photoSrc}" target="_blank" rel="noreferrer"><img src="${photoSrc}" alt="" style="width:120px;height:90px;object-fit:cover;border-radius:4px;margin-top:6px;display:block" /></a>`
        : '';
      marker.bindPopup(`
        <div style="font-family: system-ui, -apple-system, sans-serif; min-width: 180px; max-width: 240px;">
          <div style="font-weight:700;color:#052A23;font-size:13px">${escapeHtml(v.customer_name || '—')}</div>
          <div style="font-size:11px;color:#3B5A54;margin-top:2px">${dt}</div>
          ${v.geo_address ? `<div style="font-size:11px;color:#3B5A54;margin-top:2px">📍 ${escapeHtml(v.geo_address)}</div>` : ''}
          <div style="font-size:10px;color:#6D8E87;margin-top:2px;font-family:ui-monospace,monospace">${v.geo_lat.toFixed(4)}, ${v.geo_lng.toFixed(4)}</div>
          ${thumb}
        </div>
      `);
    });
    if (points.length > 0) {
      map.fitBounds(points, { padding: [24, 24], maxZoom: 15 });
    }
    // Force a resize tick — the drawer's flex container needs to settle
    // before Leaflet computes tile positions.
    setTimeout(() => map.invalidateSize(), 100);
  }, [visits]);

  useEffect(() => () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, []);

  if (visits.length === 0) {
    return (
      <div className="p-8 text-center text-sm text-[#6D8E87]">
        No visits with a valid geolocation in this window.
      </div>
    );
  }
  return (
    <div ref={mapContainer} className="w-full h-full min-h-[500px]" data-testid="visits-map" style={{ minHeight: '60vh' }} />
  );
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}


// ────────────────────────────────────────────────────────────────────────
// iter121 — Territory heatmap. Aggregates every visit across the whole
// team into a Leaflet.heat density layer. Filters: date range (week /
// month / quarter / year) and optionally a single rep. Empty state when
// no geo-tagged visits exist.
// ────────────────────────────────────────────────────────────────────────

function TerritoryHeatmapSection() {
  const [range, setRange] = useState('month');
  const [userId, setUserId] = useState('');   // '' = all
  const [members, setMembers] = useState([]);
  const [data, setData] = useState(null);
  // iter122 — Coverage gaps overlay. Threshold days is separate from the
  // heatmap range so a manager can e.g. see "last-quarter visits, but
  // uncovered = no visit in 30 days" simultaneously.
  const [showGaps, setShowGaps] = useState(true);
  const [gapDays, setGapDays] = useState(30);
  const [gaps, setGaps] = useState(null);

  const start = useMemo(() => {
    const t = new Date();
    if (range === 'week') t.setDate(t.getDate() - 6);
    else if (range === 'month') t.setDate(t.getDate() - 29);
    else if (range === 'quarter') t.setDate(t.getDate() - 89);
    else if (range === 'year') t.setDate(t.getDate() - 364);
    return t.toISOString().slice(0, 10);
  }, [range]);

  // Load the same team-matrix data to populate the "filter by rep" dropdown.
  useEffect(() => {
    axios.get(`${API}/daily-activity/matrix`)
      .then((r) => setMembers(r.data.rows || []))
      .catch(() => setMembers([]));
  }, []);

  const load = useCallback(async () => {
    setData(null);
    try {
      const params = { start, end: todayISO() };
      if (userId) params.user_id = userId;
      const r = await axios.get(`${API}/daily-activity/heatmap`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load heatmap');
      setData({ points: [], total: 0 });
    }
  }, [start, userId]);
  useEffect(() => { load(); }, [load]);

  // iter122 — coverage gaps load. Independent of heatmap range so the
  // overlay stays stable while users flip through time windows.
  const loadGaps = useCallback(async () => {
    if (!showGaps) return;
    setGaps(null);
    try {
      const r = await axios.get(`${API}/daily-activity/coverage-gaps`, { params: { days: gapDays } });
      setGaps(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load coverage gaps');
      setGaps({ cities: [], unmapped: [], total_customers: 0 });
    }
  }, [showGaps, gapDays]);
  useEffect(() => { loadGaps(); }, [loadGaps]);

  const uncoveredCount = useMemo(() => (gaps?.cities || []).reduce((s, c) => s + c.uncovered_count, 0) + (gaps?.unmapped || []).filter((u) => u.days_since_visit === null || u.days_since_visit > gapDays).length, [gaps, gapDays]);

  return (
    <div className="space-y-3" data-testid="territory-heatmap-section">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Territory heatmap</h2>
        <span className="text-[11px] text-[#6D8E87]">
          {data ? <><b>{data.total}</b> visits · {fmtDate(data.start)} → {fmtDate(data.end)}</> : 'Loading…'}
        </span>
      </div>
      <div className="medical-card p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E2ECEA] flex items-center gap-2 flex-wrap bg-[#F4F9F7]">
          {['week', 'month', 'quarter', 'year'].map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1 rounded-full text-xs font-semibold ${range === r ? 'bg-[#00a69c] text-white' : 'bg-white text-[#3B5A54] hover:bg-[#E2ECEA]'}`}
              data-testid={`heatmap-range-${r}`}
            >
              Last {r}
            </button>
          ))}
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="px-3 py-1.5 border border-[#D5E8E4] rounded-lg text-xs bg-white"
            data-testid="heatmap-user-filter"
          >
            <option value="">All team members</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>{m.user_name} — {m.user_role}</option>
            ))}
          </select>
          <div className="ml-auto inline-flex items-center gap-2 bg-white rounded-lg border border-[#D5E8E4] px-2 py-1">
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#3B5A54] cursor-pointer" title="Toggle city-level coverage circles">
              <input
                type="checkbox"
                checked={showGaps}
                onChange={(e) => setShowGaps(e.target.checked)}
                data-testid="coverage-gaps-toggle"
                className="accent-[#00a69c]"
              />
              Coverage gaps
            </label>
            <select
              value={gapDays}
              onChange={(e) => setGapDays(parseInt(e.target.value, 10))}
              disabled={!showGaps}
              className="px-2 py-0.5 border border-[#D5E8E4] rounded text-[11px] bg-white disabled:opacity-40"
              data-testid="coverage-gaps-days"
            >
              <option value={7}>7 days</option>
              <option value={15}>15 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
        </div>
        <HeatmapCanvas
          points={data?.points || []}
          loading={data === null}
          gapCities={showGaps ? (gaps?.cities || []) : []}
          gapDays={gapDays}
        />
        <div className="px-4 py-2 border-t border-[#E2ECEA] flex items-center gap-3 text-[11px] text-[#6D8E87] bg-[#F9FCFB] flex-wrap">
          <span className="font-semibold text-[#3B5A54]">Density:</span>
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#3b82f6' }} />
          <span>Low</span>
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#84cc16' }} />
          <span>Med</span>
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#f97316' }} />
          <span>High</span>
          <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#dc2626' }} />
          <span>Peak</span>
          {showGaps && (
            <>
              <span className="mx-2 text-[#94A3B8]">|</span>
              <span className="font-semibold text-[#3B5A54]">Coverage:</span>
              <span className="inline-block w-3 h-3 rounded-full border border-emerald-600" style={{ background: 'rgba(16,185,129,0.35)' }} />
              <span>Covered</span>
              <span className="inline-block w-3 h-3 rounded-full border border-amber-600" style={{ background: 'rgba(245,158,11,0.35)' }} />
              <span>Partial</span>
              <span className="inline-block w-3 h-3 rounded-full border border-red-600" style={{ background: 'rgba(220,38,38,0.35)' }} />
              <span>Gap (no visit in {gapDays}d)</span>
              {gaps && (
                <span className="ml-auto italic">
                  <b className="text-red-700">{uncoveredCount}</b> of {gaps.total_customers} customers uncovered
                </span>
              )}
            </>
          )}
        </div>
        {showGaps && gaps && (gaps.unmapped || []).length > 0 && (
          <UnmappedCustomersTray unmapped={gaps.unmapped} gapDays={gapDays} />
        )}
      </div>
    </div>
  );
}

// iter122 — Collapsible tray listing customers we couldn't place on the
// map (missing city or geocode failure). Keeps them visible so managers
// don't lose them behind the "shaded regions only" view.
function UnmappedCustomersTray({ unmapped, gapDays }) {
  const [open, setOpen] = useState(false);
  const uncovered = unmapped.filter((u) => u.days_since_visit === null || u.days_since_visit > gapDays);
  return (
    <div className="border-t border-[#E2ECEA] bg-white" data-testid="unmapped-customers-tray">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[#F4F9F7]"
        data-testid="unmapped-toggle"
      >
        <div className="text-xs font-semibold text-[#3B5A54]">
          <span className="text-amber-700">⚠</span> {unmapped.length} customer{unmapped.length === 1 ? '' : 's'} not on the map
          {uncovered.length > 0 && <span className="ml-2 text-red-700">({uncovered.length} uncovered)</span>}
        </div>
        <CaretRight size={14} className={`text-[#6D8E87] transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="max-h-56 overflow-y-auto px-4 pb-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[#6D8E87] uppercase tracking-widest text-[10px]">
                <th className="text-left py-1">Customer</th>
                <th className="text-left py-1">City</th>
                <th className="text-right py-1">Last visit</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.slice(0, 100).map((u) => (
                <tr key={u.id} className="border-t border-[#F0F5F3]">
                  <td className="py-1 font-medium text-[#052A23]">{u.name || '—'} {u.customer_code && <span className="font-mono text-[10px] text-[#6D8E87] ml-1">#{u.customer_code}</span>}</td>
                  <td className="py-1 text-[#3B5A54]">{u.city || <span className="italic text-amber-700">no city</span>}</td>
                  <td className="py-1 text-right">
                    {u.days_since_visit === null
                      ? <span className="text-red-700 font-semibold">Never</span>
                      : u.days_since_visit > gapDays
                        ? <span className="text-red-700 font-semibold">{u.days_since_visit}d ago</span>
                        : <span className="text-emerald-700">{u.days_since_visit}d ago</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function HeatmapCanvas({ points, loading, gapCities = [], gapDays = 30 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const heatLayerRef = useRef(null);
  const gapsLayerRef = useRef(null);

  // Init map + heat layer.
  useEffect(() => {
    if (!containerRef.current) return;
    if (!mapRef.current) {
      mapRef.current = L.map(containerRef.current, { attributionControl: true });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors',
      }).addTo(mapRef.current);
      // Default view — centre of India — until the first set of points lands.
      mapRef.current.setView([20.5937, 78.9629], 5);
    }
    const map = mapRef.current;
    // Remove old layer before adding new one so filter changes redraw cleanly.
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }
    if (points && points.length > 0) {
      // Leaflet.heat expects [lat, lng, intensity] — our backend already
      // matches that shape.
      heatLayerRef.current = L.heatLayer(points, {
        radius: 28,
        blur: 22,
        maxZoom: 15,
        max: Math.max(3, Math.log2(points.length + 2)),
        gradient: { 0.2: '#3b82f6', 0.4: '#84cc16', 0.65: '#f97316', 0.9: '#dc2626' },
      }).addTo(map);
    }
    setTimeout(() => map.invalidateSize(), 100);
  }, [points]);

  // Coverage-gap overlay — one circle per city. Radius scales with the
  // customer count in that city (log-scaled so a mega-city doesn't dwarf
  // a small town). Colour reflects coverage state.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (gapsLayerRef.current) {
      map.removeLayer(gapsLayerRef.current);
      gapsLayerRef.current = null;
    }
    if (!gapCities || gapCities.length === 0) {
      // Re-fit only heatmap bounds if we removed the overlay entirely.
      if (points && points.length > 0) {
        try { map.fitBounds(points.map((p) => [p[0], p[1]]), { padding: [24, 24], maxZoom: 12 }); } catch (_) {}
      }
      return;
    }
    const group = L.layerGroup();
    const colourFor = (state) => state === 'covered'
      ? { stroke: '#059669', fill: 'rgba(16,185,129,0.28)' }
      : state === 'partial'
        ? { stroke: '#b45309', fill: 'rgba(245,158,11,0.32)' }
        : { stroke: '#b91c1c', fill: 'rgba(220,38,38,0.35)' };
    gapCities.forEach((c) => {
      // Radius in metres: 8km base + 5km * sqrt(customers). Log-scaled
      // so a city with 100 customers is only ~5x bigger than one with 4.
      // Base is chosen so a city with 1–4 customers is still clearly
      // visible even at country-wide zoom (~zoom 5).
      const radius = Math.min(60000, 8000 + 5000 * Math.sqrt(Math.max(1, c.customer_count)));
      const { stroke, fill } = colourFor(c.coverage_state);
      const circle = L.circle([c.lat, c.lng], {
        radius,
        color: stroke,
        weight: 1.5,
        fillColor: fill,
        fillOpacity: 1,   // fill colour already carries alpha
      });
      const list = (c.customers || []).slice(0, 8).map((cu) => {
        const label = cu.days_since_visit === null
          ? '<span style="color:#b91c1c;font-weight:700">Never visited</span>'
          : cu.days_since_visit > gapDays
            ? `<span style="color:#b91c1c;font-weight:700">${cu.days_since_visit}d ago</span>`
            : `<span style="color:#059669;font-weight:700">${cu.days_since_visit}d ago</span>`;
        return `<li style="font-size:11px;color:#3B5A54;line-height:1.4"><b>${escapeHtml(cu.name || '—')}</b> · ${label}</li>`;
      }).join('');
      circle.bindPopup(`
        <div style="font-family: system-ui, -apple-system, sans-serif; min-width: 220px; max-width: 280px;">
          <div style="font-weight:700;color:#052A23;font-size:14px">${escapeHtml(c.city)}</div>
          <div style="font-size:11px;color:#3B5A54;margin-top:2px">
            <b>${c.customer_count}</b> customer${c.customer_count === 1 ? '' : 's'} ·
            <span style="color:${stroke};font-weight:700;text-transform:uppercase">${c.coverage_state}</span>
          </div>
          <div style="font-size:11px;color:#6D8E87;margin-top:2px">${c.covered_count} covered · <b style="color:#b91c1c">${c.uncovered_count}</b> uncovered (no visit in ${gapDays}d)</div>
          <ul style="margin:8px 0 0 14px;padding:0">${list}</ul>
          ${c.customers && c.customers.length > 8 ? `<div style="font-size:10px;color:#6D8E87;margin-top:6px">+${c.customers.length - 8} more…</div>` : ''}
        </div>
      `);
      group.addLayer(circle);
    });
    group.addTo(map);
    gapsLayerRef.current = group;
    // Re-fit map bounds so all circles are visible (in addition to points).
    const boundsPts = [
      ...points.map((p) => [p[0], p[1]]),
      ...gapCities.map((c) => [c.lat, c.lng]),
    ];
    if (boundsPts.length > 0) {
      try { map.fitBounds(boundsPts, { padding: [24, 24], maxZoom: 10 }); } catch (_) {}
    }
    setTimeout(() => map.invalidateSize(), 100);
  }, [gapCities, gapDays, points]);

  useEffect(() => () => {
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
  }, []);

  return (
    <div className="relative">
      <div ref={containerRef} className="w-full" data-testid="heatmap-canvas" style={{ height: '480px' }} />
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 text-sm text-[#6D8E87]">Loading heatmap…</div>
      )}
      {!loading && (!points || points.length === 0) && (!gapCities || gapCities.length === 0) && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm text-[#6D8E87] pointer-events-none">
          No geo-tagged visits in this window.
        </div>
      )}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// 4. Add Visit modal — photo capture + geo + customer picker + submit
// ────────────────────────────────────────────────────────────────────────

function AddVisitModal({ onClose, onSaved }) {
  const [step, setStep] = useState('picker');   // picker → capture → review
  const [customer, setCustomer] = useState(null);
  const [geo, setGeo] = useState(null);          // {lat, lng, accuracy, captured_at, address}
  const [rawPhoto, setRawPhoto] = useState(null);  // File
  const [stampedBlob, setStampedBlob] = useState(null); // stamped image Blob
  const [previewUrl, setPreviewUrl] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [captureStage, setCaptureStage] = useState('');  // human-readable step for the button label
  const fileInputRef = useRef(null);
  const currentUser = useAuthStore((s) => s.user);

  const getGeo = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) return reject(new Error('Geolocation not supported by this browser'));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy: pos.coords.accuracy || 0,
        captured_at: new Date().toISOString(),
      }),
      (err) => reject(new Error(err.message || 'Location denied')),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  });

  // Reverse-geocode via Nominatim (free, no API key). We must abide by
  // their usage policy — 1 req/s and identify ourselves with a UA + email.
  // The Referer header a browser sends already identifies the app so this
  // is only called from user-initiated captures (not batched).
  // Per the product spec: if this fails we BLOCK the visit — sales can't
  // submit a photo without a resolved address (accountability > speed).
  const reverseGeocode = async ({ lat, lng }) => {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
    const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
    if (!res.ok) throw new Error(`Address lookup failed (${res.status})`);
    const data = await res.json();
    // Prefer the compact `display_name` but strip the country + postcode
    // tail so the burned line stays readable on portrait photos.
    let display = (data.display_name || '').trim();
    const a = data.address || {};
    // Fallback synthesised address from address parts.
    const parts = [
      a.neighbourhood || a.suburb || a.village || a.hamlet || a.locality,
      a.city_district || a.town || a.city,
      a.state_district || a.state,
      a.postcode,
    ].filter(Boolean);
    const compact = parts.join(', ');
    if (!display && !compact) throw new Error('No address found for these coordinates');
    return compact || display.split(',').slice(0, 4).join(',').trim();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setSubmitting(true);
    try {
      setCaptureStage('Getting your location…');
      const g = await getGeo();
      setCaptureStage('Resolving address…');
      // Hard-block if reverse-geocode fails (user's product decision).
      const address = await reverseGeocode(g);
      const enriched = { ...g, address };
      setGeo(enriched);
      setCaptureStage('Stamping photo…');
      const stamped = await stampImage(file, enriched, currentUser?.name || '');
      setRawPhoto(file);
      setStampedBlob(stamped);
      setPreviewUrl(URL.createObjectURL(stamped));
      setStep('review');
    } catch (err) {
      // Distinct message for the "no address" case so users know it's
      // an addressing problem, not a permission or upload one.
      const msg = err.message || 'Location or photo capture failed';
      toast.error(/address/i.test(msg) ? `Couldn't resolve address — please move to a spot with signal and try again. (${msg})` : msg);
      setStep('picker');
    } finally {
      setSubmitting(false);
      setCaptureStage('');
    }
  };

  const submit = async () => {
    if (!customer || !stampedBlob || !geo) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('photo', stampedBlob, `visit-${Date.now()}.jpg`);
      fd.append('customer_id', customer.id);
      fd.append('customer_name', customer.organisation_name || customer.organisation || customer.name || '');
      fd.append('visit_date', todayISO());
      fd.append('captured_at', geo.captured_at);
      fd.append('lat', String(geo.lat));
      fd.append('lng', String(geo.lng));
      fd.append('accuracy', String(geo.accuracy || 0));
      if (geo.address) fd.append('geo_address', geo.address);
      fd.append('notes', notes);
      await axios.post(`${API}/daily-activity/entries`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Visit logged');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit visit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-2" data-testid="add-visit-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#E2ECEA] flex items-start justify-between bg-[#F4F9F7]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#92400E] font-bold">Field visit</div>
            <h2 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>
              {step === 'picker' ? '1. Pick customer' : step === 'capture' ? '2. Capture photo' : '3. Review & submit'}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#6D8E87] hover:text-[#052A23]" data-testid="add-close"><X size={22} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {step === 'picker' && (
            <CustomerPicker onPick={(c) => { setCustomer(c); setStep('capture'); }} />
          )}
          {step === 'capture' && (
            <div className="text-center py-6">
              <div className="mb-4">
                <div className="text-xs text-[#6D8E87] uppercase tracking-widest">Visiting</div>
                <div className="text-base font-bold text-[#052A23]">{customer?.organisation_name || customer?.organisation || customer?.name}</div>
              </div>
              <div className="mb-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                <MapPin size={14} className="inline mb-0.5" /> Location + address will be <b>burned</b> onto the photo. Your browser will ask for GPS on tap. Move to a spot with signal — a visit can't be logged if the address can't be resolved.
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="visit-photo-input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[#00a69c] text-white font-bold hover:bg-[#00857e] disabled:opacity-60"
                data-testid="visit-capture-btn"
              >
                <Camera size={20} weight="fill" /> {submitting ? (captureStage || 'Working…') : 'Capture photo'}
              </button>
              <button
                onClick={() => setStep('picker')}
                className="block mx-auto mt-3 text-xs text-[#6D8E87] hover:text-[#052A23]"
              >
                ← Change customer
              </button>
            </div>
          )}
          {step === 'review' && previewUrl && (
            <div className="space-y-3">
              <div className="rounded-lg overflow-hidden border border-[#E2ECEA]">
                <img src={previewUrl} alt="Visit" className="w-full h-auto" data-testid="visit-preview" />
              </div>
              <div className="text-xs text-[#3B5A54] bg-[#F4F9F7] rounded p-3 space-y-1">
                <div><b>Customer:</b> {customer?.organisation_name || customer?.organisation || customer?.name}</div>
                <div><b>Captured:</b> {fmtDateTime(geo?.captured_at)}</div>
                {geo?.address && <div><b>Address:</b> {geo.address}</div>}
                <div><b>Location:</b> {geo?.lat.toFixed(6)}, {geo?.lng.toFixed(6)} <span className="text-[#6D8E87]">(±{Math.round(geo?.accuracy || 0)} m)</span></div>
              </div>
              <textarea
                placeholder="Notes (optional) — e.g., 'Discussed product X, awaiting sample.'"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={500}
                className="w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm resize-none"
                data-testid="visit-notes"
              />
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#E2ECEA] bg-[#F4F9F7] flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded bg-white border border-[#D5E8E4] text-sm text-[#3B5A54] hover:bg-[#E2ECEA]" data-testid="add-cancel">
            Cancel
          </button>
          {step === 'review' && (
            <button
              onClick={submit}
              disabled={submitting}
              className="px-5 py-2 rounded bg-[#00a69c] text-white text-sm font-bold hover:bg-[#00857e] disabled:opacity-60"
              data-testid="add-submit"
            >
              {submitting ? 'Submitting…' : 'Log visit'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Customer picker with search + quick-add fallback
// ────────────────────────────────────────────────────────────────────────

function CustomerPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // Load once — the /api/customers endpoint returns the full visible list
  // (up to 1000 for this tenant) and has no server-side `search` filter.
  // Client-side filtering is snappy and keeps the UI responsive as the user
  // types (250ms debounce is overkill when nothing needs to reach the wire).
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API}/customers`);
        if (!cancel) setAllCustomers(r.data.customers || r.data || []);
      } catch (e) {
        if (!cancel) setAllCustomers([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  // Prefer organisation_name (the field customers are actually saved under
  // in this tenant) with fallbacks to legacy `organisation` and `name`.
  const nameOf = (c) => c.organisation_name || c.organisation || c.name || '';

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return allCustomers.slice(0, 30);
    return allCustomers.filter((c) => {
      const hay = [
        nameOf(c),
        c.customer_code,
        c.city,
        c.phone,
        c.poc_name,
        c.md_ceo_name,
        c.email,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(needle);
    }).slice(0, 50);
  }, [q, allCustomers]);

  if (showAdd) return <QuickAddCustomer onCreated={(c) => onPick(c)} onBack={() => setShowAdd(false)} />;

  return (
    <div className="space-y-3">
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-3 text-[#6D8E87]" />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search customer by name, code, city, phone…"
          className="w-full pl-9 pr-3 py-2 border border-[#D5E8E4] rounded-lg text-sm"
          data-testid="picker-search"
          autoFocus
        />
      </div>
      <div className="border border-[#E2ECEA] rounded-lg max-h-[45vh] overflow-y-auto">
        {loading && <div className="p-4 text-xs text-[#6D8E87]">Loading customers…</div>}
        {!loading && results.length === 0 && (
          <div className="p-4 text-xs text-[#6D8E87]">
            {q ? `No customers match "${q}". Add one below.` : 'No customers found. Add one below.'}
          </div>
        )}
        {results.map((c) => (
          <button
            key={c.id}
            onClick={() => onPick(c)}
            className="w-full text-left px-3 py-2 border-b border-[#E2ECEA] hover:bg-[#F4F9F7] last:border-b-0"
            data-testid={`picker-row-${c.id}`}
          >
            <div className="text-sm font-medium text-[#052A23]">{nameOf(c) || <span className="italic text-[#94A3B8]">— unnamed —</span>}</div>
            <div className="text-[11px] text-[#6D8E87]">
              {c.customer_code && <span className="font-mono mr-2">#{c.customer_code}</span>}
              {c.city || '—'} · {c.phone || 'no phone'}
              {c.gst_pending && <span className="ml-2 text-amber-700 font-bold">GST PENDING</span>}
            </div>
          </button>
        ))}
      </div>
      <button
        onClick={() => setShowAdd(true)}
        className="w-full flex items-center justify-center gap-1 px-4 py-2 rounded-lg border-2 border-dashed border-[#00a69c] text-[#00857e] font-semibold text-sm hover:bg-[#F4F9F7]"
        data-testid="picker-add-new"
      >
        <Plus size={16} /> New customer (quick add)
      </button>
    </div>
  );
}

function QuickAddCustomer({ onCreated, onBack }) {
  const [form, setForm] = useState({ name: '', phone: '', city: '', email: '' });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/daily-activity/quick-customer`, form);
      toast.success('Customer added');
      onCreated(r.data.customer);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add customer');
    } finally { setSaving(false); }
  };
  return (
    <div className="space-y-3" data-testid="quick-add-customer">
      <div className="text-xs text-[#6D8E87]">GST can be added later from the Customers page — this is a lightweight field-visit stub.</div>
      <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Customer name *" className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm" data-testid="qa-name" autoFocus />
      <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm" data-testid="qa-phone" />
      <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (optional)" className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm" data-testid="qa-email" />
      <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="w-full px-3 py-2 border border-[#D5E8E4] rounded text-sm" data-testid="qa-city" />
      <div className="flex justify-between">
        <button onClick={onBack} className="text-xs text-[#6D8E87] hover:text-[#052A23]">← Back to search</button>
        <button onClick={save} disabled={saving} className="px-4 py-1.5 rounded bg-[#00a69c] text-white text-sm font-bold hover:bg-[#00857e] disabled:opacity-60" data-testid="qa-save">
          {saving ? 'Saving…' : 'Save & continue'}
        </button>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Shared bits — StatusPill, VisitCard, canvas overlay
// ────────────────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const active = status === 'active';
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid={`status-pill-${status}`}>
      {active ? <CheckCircle size={10} weight="fill" /> : <XCircle size={10} weight="fill" />} {status}
    </span>
  );
}

function VisitCard({ visit, big, siblings }) {
  // The backend stores both `photo_url` (path form: `/api/files/…`) and
  // `photo_storage_path`. Direct `<img src="/api/files/…">` fails because
  // browsers can't set `Authorization: Bearer` headers on tags — the
  // request lands unauthenticated and the server rejects it with 401.
  // Use the shared `fileUrl()` helper which builds the full URL with the
  // JWT inlined as `?auth=…` (backend accepts the same token that way).
  const src = visit.photo_storage_path ? fileUrl(visit.photo_storage_path) : '';
  // iter127 — open the in-app lightbox instead of a new browser tab so
  // the queue stays alive on the same screen. `siblings` (if provided by
  // the parent list) lets the lightbox walk through THIS list only.
  const lightbox = usePhotoLightbox();
  const tp = useTenantPath();
  const openLightbox = () => {
    if (!src || !lightbox) return;
    const list = siblings && siblings.length > 0 ? siblings : [visit];
    const idx = Math.max(0, list.findIndex((v) => v.id === visit.id));
    lightbox.open(list, idx);
  };
  return (
    <div className={`bg-white border border-[#E2ECEA] rounded-lg overflow-hidden shadow-sm`} data-testid={`visit-card-${visit.id}`}>
      {src && (
        <button
          type="button"
          onClick={openLightbox}
          className="block w-full focus:outline-none focus:ring-2 focus:ring-[#00a69c]"
          title="Open photo"
          data-testid={`visit-photo-${visit.id}`}
        >
          <img src={src} alt={visit.customer_name} className={`w-full ${big ? 'h-56' : 'h-40'} object-cover hover:opacity-95`} loading="lazy" />
        </button>
      )}
      <div className="p-3">
        <div className="text-sm font-bold text-[#052A23] flex items-center gap-1">
          <User size={12} className="text-[#00a69c]" /> {visit.customer_name || '—'}
        </div>
        <div className="text-[11px] text-[#6D8E87] mt-1">{fmtDateTime(visit.captured_at)}</div>
        {visit.geo_address && (
          <div className="text-[11px] text-[#3B5A54] mt-1 flex items-start gap-1" title={visit.geo_address}>
            <MapPin size={11} className="flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{visit.geo_address}</span>
          </div>
        )}
        <div className="text-[10px] text-[#94A3B8] mt-1 font-mono">
          {visit.geo_lat?.toFixed(4)}, {visit.geo_lng?.toFixed(4)}
        </div>
        {visit.notes && <div className="text-xs text-[#3B5A54] mt-2 italic">"{visit.notes}"</div>}
        {/* iter131 — Last-quote chip. Latest quote across every lead
            belonging to this customer. Won leads get an ✅ badge. */}
        <LastQuoteChip lq={visit.last_quote} tp={tp} />
      </div>
    </div>
  );
}

// Small inline INR formatter that abbreviates to L / Cr for the chip.
function _fmtInrShort(n) {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2).replace(/\.00$/, '')} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(2).replace(/\.00$/, '')} L`;
  if (v >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return `₹${v.toLocaleString('en-IN')}`;
}

function LastQuoteChip({ lq, tp }) {
  // iter132/133 — a customer either has NO quote yet, OR the latest quote is
  // fresh (< tenant threshold), OR the latest quote is stale. In "no quote"
  // and "stale" cases we surface a bright teal "Draft new quote →" CTA so the
  // rep can go straight from a visit into the Add-Quote flow. Threshold is
  // tenant-configurable via Company Settings → Stale-Quote Hint (0 disables).
  const { staleQuoteDays } = useDailyActivityConfig();
  const daysAgo = (iso) => {
    if (!iso) return null;
    try { return Math.round((Date.now() - new Date(iso).getTime()) / 86400000); }
    catch { return null; }
  };
  const staleDays = staleQuoteDays;
  // Threshold=0 means the tenant has disabled the CTA entirely.
  const ctaEnabled = staleDays > 0;

  if (!lq) {
    // No quote on file at all → CTA targets the customer page (rep will
    // create a lead there first if none exists yet).
    // Rendered as a soft-CTA pill so it reads as "next action", not just info.
    return (
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-[#F1F5F4] text-[#6D8E87] border border-[#E2ECEA]"
          data-testid="last-quote-none"
          title="This customer has no quotation on file yet"
        >
          📄 No quote yet
        </span>
      </div>
    );
  }

  const dateStr = lq.quote_date
    ? new Date(lq.quote_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
    : '';
  const num = lq.quote_number || `#${(lq.quote_id || '').slice(0, 8)}`;
  const tone = lq.is_won
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100'
    : 'bg-[#F4F9F7] text-[#00857e] border-[#D5E8E4] hover:bg-[#E2F3F0]';
  const dsince = daysAgo(lq.quote_date);
  const isStale = ctaEnabled && dsince !== null && dsince > staleDays;

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <a
        href={tp(`/quotations/${lq.quote_id}`)}
        target="_blank"
        rel="noopener noreferrer"
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border transition ${tone}`}
        data-testid={`last-quote-${lq.quote_id}`}
        title={`Latest quotation for this customer — click to open${lq.is_won ? ' (Lead marked Won)' : ''}`}
      >
        {lq.is_won ? '✅' : '📄'}
        <span className="font-mono">{num}</span>
        <span className="opacity-70">·</span>
        <span>{_fmtInrShort(lq.quote_value)}</span>
        <span className="opacity-70">·</span>
        <span>{dateStr}</span>
        {lq.is_won && <span className="ml-0.5 text-emerald-700">Won</span>}
        {isStale && !lq.is_won && (
          <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 text-[9px]" title={`Last quote is ${dsince} days old`}>{dsince}d</span>
        )}
      </a>
      {/* Stale-CTA — one tap into the "add new quote for this lead" flow. */}
      {isStale && lq.lead_id && (
        <a
          href={tp(`/quotations/create?lead_id=${lq.lead_id}`)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold bg-[#00a69c] text-white border border-[#00a69c] hover:bg-[#00857e] shadow-sm"
          data-testid={`draft-new-quote-${lq.lead_id}`}
          title={`The last quote is ${dsince} days old — draft a fresh one for this lead`}
        >
          ✍ Draft new quote →
        </a>
      )}
    </div>
  );
}

// Burn geo + timestamp into the bottom-left corner of the photo.
// Uses canvas so the resulting Blob is a JPEG carrying the stamp forever.
// The "full stamp" contains user name + timestamp + address + coords —
// baked into the JPEG so it survives download, share and forwarding.
// If the photo is landscape we render three lines; if portrait we compact
// to two so the strip doesn't devour the frame.
async function stampImage(file, geo, userName = '') {
  const bmp = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bmp, 0, 0);

  // Prep strings.
  const dt = new Date(geo.captured_at).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const coords = `${geo.lat.toFixed(6)}, ${geo.lng.toFixed(6)}`;
  const addr = (geo.address || '').trim();
  // Line layout depends on aspect ratio — portrait photos get a taller
  // strip with wrapping; landscape gets a tight 3-line block.
  const lineFont = Math.max(20, Math.round(canvas.height * 0.024));
  const lineGap = Math.round(lineFont * 0.35);
  ctx.font = `600 ${lineFont}px system-ui,-apple-system,Segoe UI,Roboto,sans-serif`;
  const pad = Math.round(canvas.width * 0.025);
  const maxTextW = canvas.width - pad * 2;

  // Wrap the address if it's wider than the frame — measure and split
  // on the last space that fits.
  const wrap = (text) => {
    const words = String(text || '').split(' ');
    const lines = [];
    let cur = '';
    for (const w of words) {
      const tryLine = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(tryLine).width <= maxTextW) {
        cur = tryLine;
      } else {
        if (cur) lines.push(cur);
        cur = w;
      }
    }
    if (cur) lines.push(cur);
    return lines.slice(0, 2);   // hard cap to 2 lines for the address
  };

  const nameLine = userName ? `👤 ${userName}  ·  🕒 ${dt}` : `🕒 ${dt}`;
  const addrLines = addr ? wrap(`📍 ${addr}`) : [];
  const coordLine = `🌐 ${coords}`;

  const totalLines = 1 + addrLines.length + 1;   // name/time + address(1-2) + coords
  const barH = pad * 2 + totalLines * lineFont + (totalLines - 1) * lineGap;

  // Draw the semi-opaque black strip.
  ctx.fillStyle = 'rgba(5, 42, 35, 0.78)';
  ctx.fillRect(0, canvas.height - barH, canvas.width, barH);

  // Draw each line, top-aligned inside the strip.
  ctx.fillStyle = 'white';
  ctx.textBaseline = 'top';
  let y = canvas.height - barH + pad;
  const drawLine = (text, opts = {}) => {
    if (opts.bold) ctx.font = `700 ${lineFont}px system-ui,-apple-system,Segoe UI,Roboto,sans-serif`;
    else ctx.font = `600 ${lineFont}px system-ui,-apple-system,Segoe UI,Roboto,sans-serif`;
    ctx.fillText(text, pad, y);
    y += lineFont + lineGap;
  };
  drawLine(nameLine, { bold: true });
  addrLines.forEach((ln) => drawLine(ln));
  // Coordinates in a slightly lighter tone (opacity 0.85) so hierarchy reads.
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  drawLine(coordLine);

  return await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
}

// iter127 — Full-screen photo lightbox with keyboard nav + download.
// Renders on top of the drill drawer/matrix via portal-like fixed inset.
function PhotoLightbox({ visits, index, onIndex, onClose }) {
  const cur = visits[index];
  const src = cur?.photo_storage_path ? fileUrl(cur.photo_storage_path) : '';
  const total = visits.length;

  // Keyboard: ← → to walk, Esc to close.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
      else if (e.key === 'ArrowRight' && index < total - 1) onIndex(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, total, onIndex, onClose]);

  if (!cur) return null;
  const prev = index > 0 ? () => onIndex(index - 1) : null;
  const next = index < total - 1 ? () => onIndex(index + 1) : null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/85 flex flex-col"
      onClick={onClose}
      data-testid="photo-lightbox"
    >
      <div className="px-5 py-3 flex items-start justify-between text-white/95" onClick={(e) => e.stopPropagation()}>
        <div>
          <div className="text-sm font-semibold" style={{ fontFamily: 'Outfit' }}>{cur.customer_name || '—'}</div>
          <div className="text-xs text-white/70">{fmtDateTime(cur.captured_at)}</div>
          {cur.geo_address && <div className="text-[11px] text-white/60 mt-0.5 flex items-center gap-1"><MapPin size={11} /> {cur.geo_address}</div>}
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs text-white/70 font-mono">{index + 1} / {total}</span>
          <a
            href={src}
            download={`visit-${cur.id}.jpg`}
            className="text-white/80 hover:text-white"
            title="Download photo"
            data-testid="lightbox-download"
            onClick={(e) => e.stopPropagation()}
          >
            <DownloadSimple size={22} weight="bold" />
          </a>
          <button
            type="button"
            onClick={onClose}
            className="text-white/80 hover:text-white"
            title="Close (Esc)"
            data-testid="lightbox-close"
          >
            <X size={26} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center min-h-0 px-4" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={prev || undefined}
          disabled={!prev}
          className="text-white/70 hover:text-white disabled:opacity-20 mr-3 hidden sm:block"
          title="Previous (←)"
          data-testid="lightbox-prev"
        >
          <CaretLeft size={40} weight="bold" />
        </button>
        {src ? (
          <img
            src={src}
            alt={cur.customer_name || 'Visit'}
            className="max-h-full max-w-full object-contain rounded-md shadow-2xl"
            data-testid="lightbox-image"
          />
        ) : (
          <div className="text-white/60 italic">No photo on file for this visit.</div>
        )}
        <button
          type="button"
          onClick={next || undefined}
          disabled={!next}
          className="text-white/70 hover:text-white disabled:opacity-20 ml-3 hidden sm:block"
          title="Next (→)"
          data-testid="lightbox-next"
        >
          <CaretRight size={40} weight="bold" />
        </button>
      </div>

      <div className="px-5 py-3 text-white/80 text-xs" onClick={(e) => e.stopPropagation()}>
        {cur.notes && <div className="italic max-w-2xl">"{cur.notes}"</div>}
        <div className="mt-1 font-mono text-white/50">
          {cur.geo_lat?.toFixed(6)}, {cur.geo_lng?.toFixed(6)}
        </div>
      </div>

      <div className="absolute inset-y-0 left-0 w-1/4 sm:hidden" onClick={(e) => { e.stopPropagation(); prev && prev(); }} />
      <div className="absolute inset-y-0 right-0 w-1/4 sm:hidden" onClick={(e) => { e.stopPropagation(); next && next(); }} />
    </div>
  );
}

